<template>
  <div id="sequencing-app">
    <!-- LEFT SIDEBAR: Navigation/Sequences -->
      <vs-sidebar class="items-no-padding" parent="sequencing-app" v-model="is_navigation_sidebar_open" :hidden-background="true" position-left default-index="1" color="primary">
        <vs-divider class="d-theme-border-grey-light m-0" />
            <div class="scroll-area pt-4">
              <vx-card class="mt-4" title="Sequences by start type" @click.stop>
          <div v-for="trigger_type in triggerTypesWithSequences" :key="trigger_type.value">
            <div class="trigger-header">
              <div class="trigger-title text-truncate" :title="trigger_type.text">
                {{ trigger_type.text }}
              </div>
              <vs-button
                class="p-1 new-sequence-btn"
                v-on:click="AddSequence(trigger_type)"
                size="small"
                color="primary"
                type="flat"
              >
                <feather-icon icon="PlusIcon" svgClasses="h-4 w-4" />
              </vs-button>
            </div>
            <div class="ml-2" v-for="seq in getSequencesByTriggerType(trigger_type.value)" :key="seq.id">
              <div class="sequence-row">
                <div class="sequence-name text-truncate"
                     :class="{'active': selected_sequence && selected_sequence.id === seq.id}"
                     v-on:click="selectSequence(seq)"
                     :title="seq.name || showTriggerSubtitle(seq)">
                  <template v-if="seq.name">{{ seq.name }}</template>
                  <template v-else>{{ showTriggerSubtitle(seq) }}</template>
                </div>
                <div class="sequence-actions">
                  <feather-icon
                    icon="TrashIcon"
                    v-on:click="RemoveSequence(seq)"
                    svgClasses="h-3 w-3 cursor-pointer text-gray"
                    class="hover:text-danger"
                  />
                </div>
              </div>
            </div>
            <vs-divider class="mt-1 mb-2"></vs-divider>
          </div>
          <div v-if="triggerTypesWithSequences.length < triggerTypeOptions.length" class="mt-2">
            <vs-button 
              class="p-1 mb-1 w-full text-center"
              v-on:click="showAllTriggerTypes = !showAllTriggerTypes"
              size="small" 
              color="dark" 
              type="flat"
            >
              {{ showAllTriggerTypes ? 'Hide Empty Categories' : 'Show All Categories' }}
            </vs-button>
            <div v-if="showAllTriggerTypes">
              <div v-for="trigger_type in emptyTriggerTypes" :key="trigger_type.value">
                <vs-row vs-type="flex" vs-justify="space-between">
                  <vs-col vs-type="flex" vs-justify="left" vs-align="left" vs-w="6" class="sm:p-0 p-4 text-left text-gray">
                    <h6 :title="trigger_type.text">{{ trigger_type.text }}</h6>
                  </vs-col>
                  <vs-col vs-type="flex" vs-justify="right" vs-align="right" vs-w="6" class="sm:p-0 p-4 text-center">
                    <vs-button
                      class="p-1 mb-1 inline-flex"
                      v-on:click="AddSequence(trigger_type)"
                      size="small"
                      color="primary"
                      type="flat"
                      >New Sequence
                    </vs-button>
                  </vs-col>
                </vs-row>
              </div>
            </div>
          </div>
        </vx-card>
        <vx-card class="mt-3">
          <div class="qr-header" @click="toggleQrCode">
            <span>scan this code to test</span>
            <feather-icon :icon="showQrCode ? 'ChevronUpIcon' : 'ChevronDownIcon'" svgClasses="h-4 w-4" />
          </div>
          <div v-show="showQrCode" class="qr-content mt-2">
            <div class="qr-code align-middle text-center">
              <vue-qr
                height="150px"
                :margin="0"
                :logoSrc="hoverlayIconSrc"
                :dotScale="0.7"
                colorDark="#444444"
                :size="1000"
                :callback="getData64fromQrCode"
                :text="qrCodeUrl"
                :logoMargin="0"
                :correctLevel="3"
              ></vue-qr>
            </div>
            <div class="align-middle text-center">
              <vs-button
                size="small"
                v-clipboard:copy="qrCodeUrl"
                color="dark"
                v-clipboard:success="onQrCodeCopy"
                type="flat"
              >
                <feather-icon size="small" icon="LinkIcon" />
              </vs-button>
            </div>
          </div>
        </vx-card>
      </div>
        <vx-card class="mt-3">
          <vs-button 
            class="w-full"
            color="dark" 
            type="border" 
            @click="showJsonWindow"
          >
            <feather-icon icon="CodeIcon" class="mr-2" size="16" />
            View Sequence JSON
          </vs-button>
        </vx-card>
      </vs-sidebar>

    <!-- MAIN CONTENT AREA: Workspace + Action Inspector -->
    <div class="flex-1 flex flex-col h-full">
      <div class="flex flex-row h-full">
        <!-- CENTER: Sequence Workspace -->
        <div class="flex-1 flex flex-col items-center justify-center overflow-y-auto" style="min-width: 0;">
          <sequence-workspace
            v-if="selected_sequence"
            class="mx-auto w-full"
            :selected_sequence="selected_sequence"
            :selected_action_id="selected_action"
            :hobjects="hobjects"
            :nodePidHobjectNameOptions="nodePidHobjectNameOptions"
            :nodePidHobjectNameDictionary="nodePidHobjectNameDictionary"
            :referenceImageOptions="referenceImageOptions"
            :stencilOptions="stencilOptions"
            :triggerTypeOptions="triggerTypeOptions"
            @sequence-element-selected="handleSequenceElementSelected"
            @sequence-element-added="handleSequenceElementAdded"
            @sequence-element-removed="handleSequenceElementRemoved"
            @update-sequence-name="SaveSequenceName"
            @update-sequence="handleSequenceUpdate"
          />
        </div>
        <!-- RIGHT: Action Inspector -->
        <div class="sequencing-inspector bg-white border-l border-solid d-theme-border-grey-light h-full flex-shrink-0" style="width: 400px; min-width: 300px; max-width: 400px; overflow-y: auto;">
          <div class="mt-4" @click.stop v-if="selected_actions && selected_actions.length > 0 && !selected_action" title="Action">
            <h4 class="m-4">Select an action to change its properties</h4>
          </div>
          <div v-else-if="selected_action" @click.stop>
            <h3 class="m-4">{{ showActionSubtitle(selected_action) }}</h3>
            <div v-if="selectedActionValidation && !selectedActionValidation.isValid" class="validation-errors px-4 mb-4">
              <h4 class="text-danger mb-2">Action Requires Attention:</h4>
              <ul>
                <li v-for="(error, index) in selectedActionValidation.errors" :key="index">
                  {{ error }}
                </li>
              </ul>
            </div>
            <div>
              <vs-collapse accordion type="border">
                <vs-collapse-item>
                  <div slot="header" class="font-semibold">TIMING</div>
                  <div class="vx-row">
                    <div v-if="selected_action != selected_actions[0]" class="vx-col w-1/2">
                      <span>Start:</span>
                      <v-select
                        :clearable="false"
                        v-model="selected_action.start"
                        label="text"
                        class="w-full mb-2"
                        :options="actionsStartOptions"
                        @input="emitSequencesChanged"
                      />
                    </div>
                    <div class="vx-col w-1/2">
                      <span>Delay: (seconds)</span>
                      <vs-input
                        min="0"
                        max="512"
                        :step="0.5"
                        class="w-full mb-5"
                        v-model="selected_action.delay"
                        icon-inc="expand_less"
                        icon-dec="expand_more"
                        @change="emitSequencesChanged"
                      />
                    </div>
                    <div v-if="selected_action != selected_actions[0]" class="vx-col w-full mb-3">
                      <div v-if="selected_action.start.value == 'after_previous'">
                        Starts when previous action finishes.
                      </div>
                      <div v-else>Starts simultaneously with the previous selected_action.</div>
                    </div>
                    <div class="vx-col w-1/2">
                      <span>Repeat</span>
                      <vs-input
                        min="0"
                        max="10000"
                        :step="1"
                        class="w-full mb-5"
                        v-model="selected_action.repeat"
                        icon-inc="expand_less"
                        icon-dec="expand_more"
                      />
                    </div>
                    <div
                      v-if="selected_action.hasOwnProperty('wait_until_finished') || selected_action.type == 'action_play'"
                      class="vx-col w-full"
                    >
                      <vs-checkbox class="mb-5" v-model="waitUntilFinishedComputed"
                        ><template v-if="selected_action.type == 'action_open_url'">Wait until closed</template
                        ><template v-else>Wait until finished</template>
                      </vs-checkbox>
                    </div>
                  </div>
                </vs-collapse-item>
                <vs-collapse-item>
                  <div slot="header">
                    <div class="flex items-center">
                      <span class="mr-5 font-semibold">AFFECTED OBJECT(S)</span>
                    </div>
                  </div>
                  <div class="vx-row">
                    <div class="vx-col w-full">
                      <div v-if="!canTargetObjects(selected_action)">
                        <div>Not Applicable</div>
                      </div>
                      <div v-else>
                        <vs-checkbox class="mb-5" v-model="selected_action.target_all_objects"
                          >If checked, all objects are selected
                        </vs-checkbox>
                        <div v-show="!selected_action.target_all_objects">
                          <span>Affected Object(s):</span>
                          <v-select
                            :clearable="false"
                            v-model="selected_action.targets"
                            multiple
                            label="text"
                            class="w-full mb-2"
                            :options="nodePidHobjectNameOptions"
                          />
                        </div>
                      </div>
                    </div></div
                ></vs-collapse-item>
                <template
                  v-if="
                      selected_action.type != 'action_show' &&
                      selected_action.type != 'action_hide' &&
                      selected_action.type != 'action_toggle_show_hide' &&
                      selected_action.type != 'action_open_url' &&
                      selected_action.type != 'action_play_particles' &&
                      selected_action.type != 'action_send_amqp_message' &&
                      selected_action.type != 'action_send_mqtt_message' &&
                      selected_action.type != 'action_set_materials_variant' &&
                      selected_action.type != 'action_animate' &&
                      selected_action.type != 'action_seek' &&
                      selected_action.type != 'action_spin' &&
                      selected_action.type != 'action_orbit' &&
                      selected_action.type != 'action_transform_to' &&
                      selected_action.type != 'action_transform_by' &&
                      selected_action.type != 'action_move_in_direction' &&
                      selected_action.type != 'action_change_space' &&
                      selected_action.type != 'action_open_home' &&
                      selected_action.type != 'action_force_end_sequence' &&
                      selected_action.type != 'action_raise_event' &&
                      selected_action.type != 'action_start_following' &&
                      selected_action.type != 'action_show_instructions' &&
                      selected_action.type != 'trigger_image_anchor_found'
                  "
                ></template>
                <template v-else>
                  <vs-collapse-item>
                    <div slot="header" class="font-semibold">OPTIONS</div>
                    <div v-if="selected_action_clone" class="tab-general md:ml-4 md:mt-0 mt-0 ml-0">
                      <FormulateForm
                        v-if="selected_action.type == 'action_show'"
                        :values="selected_action_clone"
                        @events="onActionFormChanged"
                        :schema="action_show_form_schema"
                        :ferrors="{}"
                      >
                        <FormulateInput
                          v-if="selected_action.entrance_type === 'from_object_transform'"
                          type="select"
                          :options="nodePidHobjectNameDictionary"
                          placeholder = "Select an object"
                          key="hide_object_transform"
                          name="reference_object"
                          label="Object"
                          class="w-full mb-2"
                          v-model="selected_action.reference_object"
                        />
                        <FormulateInput
                          v-if="selected_action.entrance_type === 'scale_up'"
                          key="show_scale_factor"
                          name="scale_factor"
                          label="Scale mutiplier (number):"
                          v-model="selected_action.scale_factor"
                        />
                        <FormulateInput
                          v-if="selected_action.entrance_type !== 'scale_up' && selected_action.entrance_type !== 'none'"
                          key="show_distance"
                          name="distance"
                          label="Distance (meters):"
                          v-model="selected_action.distance"
                        />
                      </FormulateForm>
                      <FormulateForm
                        v-if="selected_action.type == 'action_hide'"
                        :values="selected_action_clone"
                        @events="onActionFormChanged"
                        :schema="action_hide_form_schema"
                        :ferrors="{}"
                      >
                        <FormulateInput
                          v-if="selected_action.exit_type === 'to_object_transform'"
                          type="select"
                          :options="nodePidHobjectNameDictionary"
                          placeholder = "Select an object"
                          key="hide_object_transform"
                          name="reference_object"
                          label="Object"
                          v-model="selected_action.reference_object"
                        />
                        <FormulateInput
                          v-if="selected_action.exit_type === 'scale_up' || selected_action.exit_type === 'scale_down'"
                          key="hide_scale_factor"
                          name="scale_factor"
                          label="Scale mutiplier (number):"
                          v-model="selected_action.scale_factor"
                        />
                        <FormulateInput
                          v-if="selected_action.exit_type !== 'scale_up' && selected_action.exit_type !== 'scale_down' && selected_action.exit_type !== 'none' && selected_action.exit_type !== 'fade_out' "
                          key="hide_distance"
                          name="distance"
                          label="Distance (meters):"
                          v-model="selected_action.distance"
                        />
                      </FormulateForm>
                      <FormulateForm
                        v-if="selected_action.type == 'action_toggle_show_hide'"
                        :values="selected_action_clone"
                        @events="onActionFormChanged"
                        :schema="action_toggle_show_hide_form_schema"
                        :ferrors="{}"
                      >
                        <FormulateInput
                          v-if="selected_action.exit_type === 'to_object_transform' || selected_action.entrance_type === 'from_object_transform'"
                          type="select"
                          :options="nodePidHobjectNameDictionary"
                          placeholder = "Select an object"
                          key="toggle_show_hide_object_transform"
                          name="reference_object"
                          label="Object"
                          v-model="selected_action.reference_object"
                        />
                        <FormulateInput
                          v-if="selected_action.exit_type === 'scale_up' || selected_action.exit_type === 'scale_down' || selected_action.entrance_type === 'scale_up' || selected_action.entrance_type === 'scale_down'"
                          key="toggle_show_hide_scale_factor"
                          name="scale_factor"
                          label="Scale mutiplier (number):"
                          v-model="selected_action.scale_factor"
                        />
                        <FormulateInput
                          v-if="selected_action.exit_type !== 'scale_up' && selected_action.exit_type !== 'scale_down' && selected_action.entrance_type !== 'scale_down' && selected_action.entrance_type !== 'scale_up'  && selected_action.exit_type !== 'none' && selected_action.exit_type !== 'fade_out' && selected_action.entrance_type !== 'fade_in'"
                          key="toggle_show_hide_distance"
                          name="distance"
                          label="Distance (meters):"
                          v-model="selected_action.distance"
                        />
                      </FormulateForm>
                      <FormulateForm
                        v-if="selected_action.type == 'action_seek'"
                        :values="selected_action_clone"
                        @events="onActionFormChanged"
                        :schema="action_seek_form_schema"
                      />
                      <FormulateForm
                        v-if="selected_action.type == 'action_spin'"
                        :values="selected_action_clone"
                        @events="onActionFormChanged"
                        :schema="action_spin_form_schema"
                      />
                      <FormulateForm
                        v-if="selected_action.type == 'action_orbit'"
                        :values="selected_action_clone"
                        @events="onActionFormChanged"
                        :schema="action_orbit_form_schema"
                      />
                      <FormulateForm
                        v-if="selected_action.type == 'action_transform_to'"
                        :values="selected_action_clone"
                        @events="onActionFormTransformToSubmitted"
                        :schema="action_transform_to_form_schema"
                      />
                      <FormulateForm
                        v-if="selected_action.type == 'action_transform_by'"
                        :values="selected_action_clone"
                        @events="onActionFormTransformBySubmitted"
                        :schema="action_transform_by_form_schema"
                      />
                      <FormulateForm
                        v-if="selected_action.type == 'action_move_in_direction'"
                        :values="selected_action_clone"
                        @events="onActionFormTransformBySubmitted"
                        :schema="action_move_in_direction_form_schema"
                      />
                      <FormulateForm
                        v-if="selected_action.type == 'action_set_materials_variant'"
                        :values="selected_action_clone"
                        :schema="action_set_materials_variant_form_schema"
                        @events="onActionFormChanged"
                      />
                      <FormulateForm
                        v-if="selected_action.type == 'action_animate'"
                        :values="selected_action_clone"
                        :schema="action_animate_form_schema"
                        @events="onActionFormChanged"
                      />
                      <FormulateForm
                        v-if="selected_action.type == 'action_play_particles'"
                        :values="selected_action_clone"
                        :schema="action_play_particles_form_schema"
                        @events="onActionFormChanged"
                      />
                      <FormulateForm
                        v-if="selected_action.type == 'action_open_url'"
                        :values="selected_action_clone"
                        :schema="action_open_url_form_schema"
                        @events="onActionFormChanged"
                      />
                      <FormulateForm
                        v-if="selected_action.type == 'action_change_space'"
                        :values="selected_action_clone"
                        :schema="action_change_space_form_schema"
                        @events="onActionFormChanged"
                      />
                      <FormulateForm
                        v-if="selected_action.type == 'action_open_home'"
                        @events="onActionFormChanged">
                        <FormulateInput
                          type="checkbox"
                          name="highlight_space"
                          label="Highlight a specific space"
                          class="flex"
                          v-model="selected_action.highlight_space"
                        />
                        <FormulateInput
                          v-if="selected_action.highlight_space"
                          type="select"
                          name="space_pid"
                          label="Space to highlight"
                          :options=geolocatedSpacePidDictionary
                          placeholder = "Select a space"
                          class="w-full mb-2"
                          v-model="selected_action.space_pid"
                        />
                      </FormulateForm>
                      <FormulateForm
                        v-if="selected_action.type == 'action_force_end_sequence'"
                        :values="selected_action_clone"
                        :schema="action_force_end_sequence_form_schema"
                        @events="onActionFormChanged"
                      />
                      <FormulateForm
                        v-if="selected_action.type == 'action_show_instructions'"
                        :values="selected_action_clone"
                        :schema="action_show_instructions_form_schema"
                        @events="onActionFormChanged"
                      />
                      <FormulateForm
                        @events="onActionFormChanged"
                      />
                      <FormulateForm
                        v-if="selected_action.type == 'action_raise_event'"
                        :values="selected_action_clone"
                        :schema="action_raise_event_form_schema"
                        @events="onActionFormChanged"
                      />
                      <FormulateForm
                        v-if="selected_action.type == 'action_start_following'"
                        :values="selected_action_clone"
                        :schema="action_start_following_form_schema"
                        @events="onActionFormChanged"
                      />
                      <FormulateForm
                        v-if="selected_action.type == 'trigger_image_anchor_found'"
                        :values="selected_action_clone"
                        :schema="trigger_image_anchor_found_form_schema"
                        @events="onActionFormChanged"
                      />
                      <div v-if="selected_action.type == 'action_show_instructions'">
                        <span>Instructions image (optional):</span>
                        <file-pond
                          :imagePreviewHeight="200"
                          name="test"
                          ref="instructionImageFilePond"
                          :label-idle="this.$t('HoverpackImageDrag')"
                          :allow-multiple="false"
                          :instant-upload="false"
                          accepted-file-types="image/jpeg, image/png"
                          v-on:removefile="onInstructionImageRemoved"
                          v-on:addfile="onInstructionImageAdded"
                          v-on:init="onFilePondInit"
                        />
                      </div>
                    </div>
                  </vs-collapse-item>
                </template>
              </vs-collapse>
            </div>
          </div>
          <div v-else>
            <h4 class="m-4">To create an action click on "Add Action"</h4>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import 'vue-json-pretty/lib/styles.css'
import { uuid } from 'vue-uuid'
import vSelect from 'vue-select'
import VxCard from '../../../../components/vx-card/VxCard.vue'
import 'vue-select-image/dist/vue-select-image.css'
import '@braid/vue-formulate/themes/snow/snow.scss'
import { io } from 'socket.io-client'
import { v4 as uuidv4 } from 'uuid'

// Import constants from the constants folder
import { SEQUENCE_ELEMENT_ICONS } from './constants/sequence-element-icons'
import { getActionDescription, getActionClass, canTargetObjects, validateSequenceElement } from './utils/sequence-element-utils';
import { 
  triggerTypesDictionary, 
  experienceTriggerTypesDictionary,
  objectTriggerTypesDictionary,
  shouldTriggerRepeat
} from './constants/trigger-types'
import {
  actionTypesDictionary,
  actionStartDictionary,
  getAvailableActionTypes,
  requiresTargetObjects
} from './constants/action-types'

// Forms
import { actionShowSchema } from './forms/ActionShow.js'
import { actionHideSchema } from './forms/ActionHide.js'
import { actionToggleShowHideSchema } from './forms/ActionToggleShowHide.js'
import { actionSeekSchema } from './forms/ActionSeek.js'
import { actionSetMaterialsVariantSchema } from './forms/ActionSetMaterialsVariant.js'
import { actionAnimateSchema } from './forms/ActionAnimate.js'
import { actionSpinSchema } from './forms/ActionSpin.js'
import { actionOrbitSchema } from './forms/ActionOrbit.js'
import { actionTransformToSchema } from './forms/ActionTransformTo.js'
import { actionTransformBySchema } from './forms/ActionTransformBy.js'
import { actionMoveInDirectionSchema } from './forms/ActionMoveInDirection.js'
import { actionPlayParticlesSchema } from './forms/ActionPlayParticles.js'
import { actionOpenUrlSchema } from './forms/ActionOpenUrl.js'
import { actionChangeSpaceSchema } from './forms/ActionChangeSpace.js'
import { actionForceEndSequenceSchema } from './forms/ActionForceEndSequence.js'
import { actionShowInstructionSchema } from './forms/ActionShowInstruction.js'
import { actionRaiseEventSchema } from './forms/ActionRaiseEvent.js'
import { actionStartFollowingSchema } from './forms/ActionStartFollowing.js'
import {triggerImageAnchorFoundSchema} from './forms/TriggerImageAnchorFound.js'

// Sequencing utils
import { createSequenceElement } from './utils/sequence-element-factory' 

// Sequencing components
import SequenceWorkspace from './components/SequenceWorkspace.vue'

import * as Utils from '@/assets/js/utils/utils.js'
import * as HoverlayUtils from '@/assets/js/utils/hoverlay-utils.js'
import * as HoverlayThreeUtils from '@/assets/js/utils/hoverlay-three-utils.js'
import imageCompression from 'browser-image-compression'

// Form events
import { ref } from '@vue/composition-api'
import Avatar from '../../../components/vuesax/avatar/Avatar.vue'

// Import Vue FilePond
import vueFilePond from 'vue-filepond'

// Import FilePond styles
import 'filepond/dist/filepond.min.css'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css'

import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation'
import FilePondPluginImageTransform from 'filepond-plugin-image-transform'
import FilePondPluginImageResize from 'filepond-plugin-image-resize'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginFileEncode from 'filepond-plugin-file-encode';
import SelectAutocomplete from '@/views/forms/form-elements/select/SelectAutocomplete.vue'

// Create component
const FilePond = vueFilePond(
  FilePondPluginFileValidateType,
  FilePondPluginImagePreview,
  FilePondPluginImageExifOrientation,
  FilePondPluginImageResize,
  FilePondPluginImageTransform,
  FilePondPluginFileEncode
)

export default {
  inject: ['$validator'],
  emits: [
    'sequences-changed'
  ],
  data() {
    let role = HoverlayUtils.getUserRole();
    
    // Use imported trigger dictionaries instead of defining them inline
    // Filter experienceTriggerTypesDic based on user role
    let experienceTriggerTypesDic = { ...experienceTriggerTypesDictionary };
    
    // Only include admin/staff trigger types if user has appropriate role
    if (role !== 'staff' && role !== 'admin') {
      delete experienceTriggerTypesDic.trigger_user_aligned_anchor;
      delete experienceTriggerTypesDic.trigger_mqtt_message_received;
      delete experienceTriggerTypesDic.trigger_amqp_message_received;
    }
    
    let objectTriggerTypesDic = { ...objectTriggerTypesDictionary };
    let allTriggerTypesDic = {
      ...experienceTriggerTypesDic,
      ...objectTriggerTypesDic
    };
    
    // Get available action types based on user role
    let actionTypesDic = getAvailableActionTypes(role);

    return {
      socket: null,
      editorSessionId: null,
      hoverlayIconSrc: require('@/assets/images/hoverlay/construction.png'),
      qrCodeUrl: null,
      events: ref([]),
      values: {},
      action_show_form_schema: actionShowSchema,
      action_hide_form_schema: actionHideSchema,
      action_toggle_show_hide_form_schema: actionToggleShowHideSchema,
      action_seek_form_schema: actionSeekSchema,
      action_set_materials_variant_form_schema: actionSetMaterialsVariantSchema,
      action_animate_form_schema: actionAnimateSchema,
      action_spin_form_schema: actionSpinSchema,
      action_orbit_form_schema: actionOrbitSchema,
      action_transform_to_form_schema: actionTransformToSchema,
      action_transform_by_form_schema: actionTransformBySchema,
      action_move_in_direction_form_schema: actionMoveInDirectionSchema,
      action_play_particles_form_schema: actionPlayParticlesSchema,
      action_open_url_form_schema: actionOpenUrlSchema,
      action_change_space_form_schema: actionChangeSpaceSchema,
      action_force_end_sequence_form_schema: actionForceEndSequenceSchema,
      action_show_instructions_form_schema: actionShowInstructionSchema,
      action_raise_event_form_schema: actionRaiseEventSchema,
      action_start_following_form_schema: actionStartFollowingSchema,
      trigger_image_anchor_found_form_schema: triggerImageAnchorFoundSchema,
      layer_name: JSON.parse(localStorage.getItem('layerInfo')).name,
      selected_sequence: null,
      actionTabIndex: 1,
      selected_actions: null,
      selected_action: null,
      selected_action_clone: null,
      selected_action_type: { text: 'Animate Entrance', value: 'action_show' },
      sequences: [],
      triggerTypeOptions: [],
      selectedReferenceImage: null,
      referenceImageOptions: null,
      selectedSencil: null,
      stencilOptions: null,
      actionsStartDic: actionStartDictionary,
      spacePidNameOptions: [],
      geolocatedSpacePidOptions: [],
      entranceTypeOptions: [],
      exitTypeOptions: [],
      nodePidHobjectNameOptions: [],
      actionsStartOptions: [],
      axisOptions: [],
      directionOptions: [],
      axisDic: {
        right: 'Right',
        up: 'Up',
        rear: 'Rear',
      },
      directionDic: {
        clockwise: 'Clockwise',
        counterclockwise: 'Counterclockwise',
      },
      experienceTriggerTypesDic,
      objectTriggerTypesDic,
      allTriggerTypesDic,
      actionTypesDic,
      actionTypes: [],
      is_action_inspector_open: ref(false),
      is_navigation_sidebar_open: ref(true),
      show_vue_sequencing_json: false,
      is_sequence_name_editing: false,
      temp_sequence_name: null,
      showAllTriggerTypes: false,
      showQrCode: true,
    }
  },
  props: {
    space: {},
    hobjects: {},
    nodes: {},
    anchors_spaces: {},
    anchors: {},
    nodePidHobjectNameDictionary: {},
    spacePidNameDictionary: {},
    geolocatedSpacePidDictionary: {},
  },
  components: {
    'v-select': vSelect,
    VxCard,
    SequenceWorkspace,
  },
  watch: {
    selected_sequence: function(updatedValue) {
      if (updatedValue) this.selected_actions = updatedValue.actions
    },
    selected_action: function(updatedValue) {
      this.selected_action_clone = null
      // Clone selected_action in the next tick (to overcome vue-formulate issue)
      this.$nextTick(() => {
        this.selected_action_clone = JSON.parse(JSON.stringify(updatedValue))
      })
    },
  },
  computed: {
    space_pid: function() {
      return this.$route.params.pid
    },
    isSmallerScreen() {
      return this.$store.state.windowWidth < 768
    },
    userRole() {
      return HoverlayUtils.getUserRole()
    },
    waitUntilFinishedComputed: {
      get() {
        // Return true if undefined, otherwise return the actual value
        return this.selected_action.wait_until_finished === undefined ? 
               true : 
               this.selected_action.wait_until_finished;
      },
      set(value) {
        // When changed, update the value in the action
        this.selected_action.wait_until_finished = value;
        // Mark as dirty when timing properties are changed
        this.emitSequencesChanged();
      }
    },
    selectedActionValidation() {
      if (!this.selected_action) return null;
      return validateSequenceElement(this.selected_action);
    },
    triggerTypesWithSequences() {
      return this.triggerTypeOptions.filter(triggerType => 
        this.sequences.some(seq => seq.trigger.trigger_type.value === triggerType.value)
      );
    },
    emptyTriggerTypes() {
      return this.triggerTypeOptions.filter(triggerType => 
        !this.sequences.some(seq => seq.trigger.trigger_type.value === triggerType.value)
      );
    },
  },
  mounted() {},
  async created() {
    // Populate dictionnaries
    for (const [key, value] of Object.entries(this.actionTypesDic)) {
      this.actionTypes.push({ text: value, value: key })
    }
    for (const [key, value] of Object.entries(this.spacePidNameDictionary)) {
      this.spacePidNameOptions.push({ text: value, value: key })
    }
    for (const [key, value] of Object.entries(this.nodePidHobjectNameDictionary)) {
      this.nodePidHobjectNameOptions.push({ text: value, value: key })
    }
    for (const [key, value] of Object.entries(this.allTriggerTypesDic)) {
      this.triggerTypeOptions.push({ text: value, value: key })
    }
    for (const [key, value] of Object.entries(this.actionsStartDic)) {
      this.actionsStartOptions.push({ text: value, value: key })
    }
    for (const [key, value] of Object.entries(this.directionDic)) {
      this.directionOptions.push({ text: value, value: key })
    }
    for (const [key, value] of Object.entries(this.axisDic)) {
      this.axisOptions.push({ text: value, value: key })
    }

    // Hide action inspector
    this.is_action_inspector_open = false

    this.action_change_space_form_schema[0].options = this.spacePidNameDictionary
    // Populate reference for ActionChangeSpace when keep_origin is true
    // this.action_change_space_form_schema[2].options = this.nodePidHobjectNameDictionary

    this.referenceImageOptions = []
    this.anchors_spaces.forEach(as => {
      if (as.anchor.anchor_type == 'image' || as.anchor.anchor_type == 'vuforia-instant-image-target')
        this.referenceImageOptions.push({
          id: as.anchor.pid,
          name: as.anchor.name,
          src: as.anchor.data,
          value: as.anchor.pid,
          alt: 'none',
        })
    })

    this.stencilOptions = []
    this.anchors_spaces.forEach(as => {
      if (as.anchor.anchor_type == 'user-aligned-anchor')
        this.stencilOptions.push({
          id: as.anchor.pid,
          name: as.anchor.name,
          src: as.anchor.data,
          value: as.anchor.pid,
          alt: 'none',
        })
    })

    // console.log(this.space.storylines)
    if (
      this.space.storylines == null ||
      this.space.storylines.sequences == null ||
      !this.isValid(this.space.storylines.sequences)
    ) {
      this.AddSequence(null)
    } else {
      this.sequences = this.deserialize(this.space.storylines.sequences)
    }
    this.selectSequence(this.sequences[0])

    this.editorSessionId = localStorage.getItem('editorSessionId')
    if (!this.editorSessionId) {
      localStorage.setItem('editorSessionId', uuidv4())
      this.editorSessionId = localStorage.getItem('editorSessionId')
    }

    this.qrCodeUrl = `https://hoverlay.io/space/${this.space.pid}?editor_token=${this.editorSessionId}-${this.space.pid}`
    // connect to the websocket
    this.socket = io('https://hov-event-bridge.herokuapp.com/storyline-webapp', {
      query: {
        editor_token: `${this.editorSessionId}-${this.space.pid}`,
      },
    })
    this.socket.on('send_event', args => {
      args = JSON.parse(args)
      if (args.event_name == 'onSequenceElementStarted') {
        var element = document.getElementById(args.sequence_element_id)
        if (element) element.classList.add('sequence-element-started')
      } else if (args.event_name == 'onSequenceElementEnded') {
        element = document.getElementById(args.sequence_element_id)
        if (element) element.classList.remove('sequence-element-started')
      } else if (args.event_name == 'onSequenceElementCanceled') {
        element = document.getElementById(args.sequence_element_id)
        if (element) element.classList.add('sequence-element-canceled')
      }
    })

    // Populate ActionForceEndSequenceFormSchema with sequences dictionary (name and id)
    this.action_force_end_sequence_form_schema[0].options = this.sequences.map(seq => ({
      label: seq.name,
      value: seq.id,
    }))
  },
  methods: {
    startSequence(sequence_index) {
      console.log('startSequence')
      this.socket.emit('start_sequence', {
        editor_token: this.editorSessionId,
        sequence_index,
      })
    },
    async onQrCodeDownload() {
      var res = await fetch(this.qrCodeData64)
      var blob = await res.blob()
      var a = document.createElement('a')
      document.body.appendChild(a)
      a.style = 'display: none'

      var url = window.URL.createObjectURL(blob)
      a.href = url
      a.download = this.space.name
      a.click()
      window.URL.revokeObjectURL(url)
    },
    onQrCodeCopy() {
      this.$vs.notify({
        title: 'Success',
        text: this.$t('QrTestCodeCopy'),
        color: 'success',
        iconPack: 'feather',
        position: 'bottom-left',
        icon: 'icon-check-circle',
      })
    },
    getData64fromQrCode(data64, _) {
      this.qrCodeData64 = data64
    },
    showActionSubtitle(action) {
      var text = ''
      var hobjectName = ''
      var othersHobject = ''
      var verbTermination = ''

      if (action.target_all_objects == true) {
        hobjectName = 'All objects'
      } else {
        if (action.targets) {
          hobjectName = action.targets[0] ? action.targets[0].text : ''
          if (action.targets.length == 2) {
            othersHobject = `(+ ${action.targets.length - 1}) `
          }
          if (action.targets.length > 2) {
            othersHobject = `(+ ${action.targets.length - 1})`
          }
          verbTermination = action.targets.length == 1 ? 'S' : ''
        }
      }

      if (action.type == 'action_show') text = `${hobjectName} ${othersHobject} ENTER` + verbTermination
      if (action.type == 'action_hide') text = `${hobjectName} ${othersHobject} EXIT` + verbTermination
      if (action.type == 'action_toggle_show_hide') text =  `${hobjectName} ${othersHobject} TOGGLE SHOW/HIDE` + verbTermination
      if (action.type == 'action_play') text = `${hobjectName} ${othersHobject} PLAY` + verbTermination
      if (action.type == 'action_pause') text = `${hobjectName} ${othersHobject} PAUSE` + verbTermination
      if (action.type == 'action_toggle_play_pause') text = `${hobjectName} ${othersHobject} TOGGLE PLAY/PAUSE` + verbTermination
      if (action.type == 'action_stop') text = `${hobjectName} ${othersHobject} STOP` + verbTermination
      if (action.type == 'action_seek') text = `SET START TIME on ${hobjectName} ${othersHobject} at ${action.seconds} sec`
      if (action.type == 'action_set_materials_variant') text = `SET MATERIALS VARIANT on ${hobjectName} ${othersHobject}`
      if (action.type == 'action_animate') text = `PLAY ANIMATION on ${hobjectName} ${othersHobject}`
      if (action.type == 'action_spin') text = `SPIN on ${hobjectName} ${othersHobject}`
      if (action.type == 'action_orbit') text = `ORBIT on ${hobjectName} ${othersHobject}`
      if (action.type == 'action_transform_to') text = `Move, Rotate, Scale to`
      if (action.type == 'action_transform_by') text = `Move, Rotate, Scale by`
      if (action.type == 'action_move_in_direction') text = `Move in direction`
      if (action.type == 'action_play_particles') text = `PLAY PARTICLES on ${hobjectName} ${othersHobject}`
      if (action.type == 'action_send_amqp_message') text = `SEND ${action.message} to ${action.host}`
      if (action.type == 'action_send_mqtt_message') text = `SEND ${action.message} to ${action.host}`
      if (action.type == 'action_change_space') text = 'CHANGE SPACE'
      if (action.type == 'action_open_home') text = 'OPEN HOME PAGE'
      if (action.type == 'action_force_end_sequence') {
        const sequence = this.sequences.find(seq => seq.id === action.sequence_id)
        const sequenceName = sequence ? sequence.name : 'Unknown Sequence'
        text = `FORCE END SEQUENCE ${sequenceName}`
      }
      if (action.type == 'action_show_instructions') text = 'SHOW INSTRUCTIONS'
      if (action.type == 'action_open_url') text = 'OPEN LINK'
      if (action.type == 'action_raise_event') text = `RAISE EVENT ${action.event_message}`
      if (action.type == 'action_set_experience_origin') text = 'RESET USER ORIGIN'
      if (action.type == 'action_start_following') text = `Attach ${hobjectName} ${othersHobject} to Screen`
      if (action.type == 'action_stop_following') text = `Detach ${hobjectName} ${othersHobject} from Screen`

      return text
    },
    removeTrigger: function(t) {
      console.log('removeTrigger')
      const index = this.sequences.indexOf(t)
      if (index > -1) {
        this.sequences.splice(index, 1)
      }

      if (this.selected_sequence == null && this.sequences.length > 0) this.selectSequence(this.sequences[0])
    },
    removeAction: function(a) {
      if (this.selected_action && this.selected_action.id == a.id) this.selected_action = undefined
      this.sequences.forEach(trigger => {
        var action = trigger.actions.find(action => action.id == a.id)
        if (action) {
          const index = trigger.actions.indexOf(action)
          if (index > -1) {
            trigger.actions.splice(index, 1)
          }
        }
      })
    },
    RemoveSequence: function(sequence) {
      var index
      var tmp
      if (sequence) tmp = this.sequences.find(seq => seq.id == sequence.id)
      else tmp = this.sequences.find(seq => seq.id == this.selected_sequence.id)
      index = this.sequences.indexOf(tmp)

      if (index > 0) {
        this.selectSequence(this.sequences[index - 1])
      } else {
        this.selectSequence(null)
      }
      if (index > -1) {
        this.sequences.splice(index, 1) // 2nd parameter means remove one item only
      }

      // Unselect action
      this.SelectAction(null)

      // Mark as dirty when sequences are removed
      this.emitSequencesChanged()
    },
    AddSequence: function(trigger_type) {
      if (!trigger_type) trigger_type = { value: 'trigger_experience_starts', text: 'Immediately' }

      // Use the shouldTriggerRepeat function from the imported constants
      const repeat = shouldTriggerRepeat(trigger_type.value);

      this.sequences.push({
        name: `Sequence ${this.sequences.length + 1}`,
        actions: [],
        trigger: { trigger_type: trigger_type, id: uuid.v4() },
        id: uuid.v4(),
        repeat: repeat,
      })
      this.selectSequence(this.sequences[this.sequences.length - 1])
      // Mark as dirty when new sequences are added
      this.emitSequencesChanged()
    },
    // Emit changes to parent component for isDirty tracking
    emitSequencesChanged() {
      this.$emit('sequences-changed');
    },
    handleSequenceElementSelected(data) {
      this.SelectAction(data.action.id)
      this.selected_action = data.action
      this.is_action_inspector_open = true
      this.actionTabIndex = 0
    },
    handleSequenceElementAdded(data) {
      console.log('handleSequenceElementAdded')
      console.log(data)
      this.selected_action = data.action
      this.is_action_inspector_open = true
      this.actionTabIndex = 1
      // Mark as dirty when sequence elements are added
      this.emitSequencesChanged()
    },
    handleSequenceElementRemoved(data) {
      this.selected_action = null
      this.is_action_inspector_open = false
      // Mark as dirty when sequence elements are removed
      this.emitSequencesChanged()
    },
    handleSequenceUpdate(updatedSequence) {
      // Find the sequence in the sequences array
      const index = this.sequences.findIndex(seq => seq.id === updatedSequence.id);

      if (index !== -1) {
        // Replace the sequence with the updated version
        this.$set(this.sequences, index, updatedSequence);

        // If this is currently selected sequence, update that reference too
        if (this.selected_sequence && this.selected_sequence.id === updatedSequence.id) {
          this.selected_sequence = updatedSequence;

          // If actions were changed, ensure we update the actions array
          if (updatedSequence.actions) {
            this.selected_actions = updatedSequence.actions;
          }
        }

        // Mark as dirty when sequences are updated
        this.emitSequencesChanged()

        // Optional: Add notification if you want to confirm changes
        // this.$vs.notify({
        //   title: 'Sequence Updated',
        //   text: 'Sequence properties have been updated',
        //   color: 'success',
        //   position: 'bottom-right',
        //   icon: 'check_circle'
        // });
      }
    },
    AddAction(selected_action_type) {
      // Suggest potential targets for that action
      const targets = { text: this.nodePidHobjectNameDictionary[this.nodes[0].pid], value: this.nodes[0].pid }

      // Create the sequence element and add to the selected sequence
      var element = createSequenceElement(selected_action_type, targets)
      this.selected_sequence.actions.push(element)

      // Then select the action we just added
      this.SelectAction(element.actionId)
      // Selected second action tab by default
      this.actionTabIndex = 1
    },
    selectSequence(sequence) {
      this.selected_sequence = sequence
      this.is_action_inspector_open = false
    },
    getBackgroundColor() {
      return '#2816df'
      // if (this.selected_sequence && this.selected_sequence.id == trigger.id) return '#8379f2'
      // else return '#e9e7fd'
    },
    async updateSpace() {
      // Validate all sequence elements
      let hasValidationErrors = false;
      const validationSummary = [];
      
      this.sequences.forEach(sequence => {
        if (sequence.actions && sequence.actions.length > 0) {
          sequence.actions.forEach(action => {
            const result = validateSequenceElement(action);
            if (!result.isValid) {
              hasValidationErrors = true;
              validationSummary.push(`"${getActionDescription(action)}": ${result.errors.join(', ')}`);
            }
          });
        }
      });
      
      // If there are validation errors, show a warning
      if (hasValidationErrors) {
        // Show maximum 3 errors in the notification to avoid overwhelming the user
        const displayErrors = validationSummary.slice(0, 3);
        const additionalErrors = validationSummary.length > 3 ? 
          `\nand ${validationSummary.length - 3} more issues` : '';
        
        this.$vs.dialog({
          type: 'confirm',
          color: 'warning',
          title: 'Validation Issues Found',
          text: `Your sequence has ${validationSummary.length} issue(s) that may cause problems when playing:\n\n${displayErrors.join('\n')}${additionalErrors}\n\nDo you want to save anyway?`,
          acceptText: 'Save Anyway',
          cancelText: 'Cancel',
          async accept() {
            // User chose to save anyway
            await this.$store.dispatch('hoverlay/updateSpace', {
              pid: this.space_pid,
              storylines: { sequences: this.buildJsonSequence() },
            });
            
            this.$vs.notify({
              text: 'Sequences saved with validation issues',
              color: 'warning',
              icon: 'warning',
            });
          },
          cancel() {
            // User canceled the save operation
          }
        });
      } else {
        // No validation errors, proceed with save
        await this.$store.dispatch('hoverlay/updateSpace', {
          pid: this.space_pid,
          storylines: { sequences: this.buildJsonSequence() },
        });
        
        this.$vs.notify({
          text: 'Sequences saved',
          color: 'success',
          icon: 'check_box',
        });
      }
    },
    getActionClass(action) {
      return getActionClass(action)
    },
    canTargetObjects(actionType) {
      return canTargetObjects(actionType)
    },

    // DESERIALIZER
    deserialize: function(json) {
      // json = [
      //   {
      //     type: 'sequential',
      //     elements: [
      //       { type: 'trigger_experience_starts', targets: [{ text: '4ee99d8a-e-RealityLab', value: 'gtceb' }] },
      //       {
      //         type: 'parallel',
      //         elements: [
      //           { type: 'show', targets: ['gtceb'], duration: 1, delay: 3 },
      //           { type: 'hide', targets: ['gtceb'], duration: 2, delay: 4 },
      //         ],
      //       },
      //       // {
      //       //   type: 'parallel',
      //       //   elements: [
      //       //     { type: 'show3', targets: ['gtceb'], duration: 0, delay: 0 },
      //       //     { type: 'show4', targets: ['gtceb'], duration: 0, delay: 0 },
      //       //   ],
      //       // },
      //     ],
      //   },
      // ]

      var sequences = []

      json.forEach(sequence => {
        var s = {
          trigger: {
            id: sequence.elements[0].id,
            trigger_type: { value: sequence.elements[0].type, text: this.allTriggerTypesDic[sequence.elements[0].type] },
          },
          targets: this.getElementTargets(sequence.elements[0]),
          target: this.getElementTarget(sequence.elements[0]),
          actions: [],
          id: sequence.id,
          name: sequence.name,
        }
        // trigger_image_anchor_found
        // trigger_image_anchor_lost
        if (
          sequence.elements[0].type == 'trigger_image_anchor_found' ||
          sequence.elements[0].type == 'trigger_image_anchor_lost'
        ) {
          if (sequence.elements[0].anchor != null) {
            var a = this.referenceImageOptions.find(ref => ref.id == sequence.elements[0].anchor)
            s.anchor = a
            var anchor = this.anchors.find(a => a.pid == sequence.elements[0].anchor)
            s.selectedReferenceImage = {
              id: anchor.pid,
              src: anchor.data,
              value: anchor.pid,
              alt: 'none',
            }
          }
        }
        if (
          sequence.elements[0].type == 'trigger_user_aligned_anchor'
        ) {
          if (sequence.elements[0].anchor != null) {
            var stencil = this.stencilOptions.find(ref => ref.id == sequence.elements[0].anchor)
            s.anchor = stencil
            var userAlignedAnchor = this.anchors.find(a => a.pid == sequence.elements[0].anchor)
            s.selectedSencil = {
              id: userAlignedAnchor.pid,
              src: userAlignedAnchor.data,
              value: userAlignedAnchor.pid,
              alt: 'none',
            }
          }
        }
        if (
          sequence.elements[0].type == 'trigger_image_anchor_found' ||
          sequence.elements[0].type == 'trigger_image_anchor_lost' ||
          sequence.elements[0].type == 'trigger_user_aligned_anchor' ||
          sequence.elements[0].type == 'trigger_screen_tap'
        ) {
          s.instructions_text = sequence.elements[0].instructions_text
          s.show_instructions = sequence.elements[0].show_instructions
          s.instructions_image_base64 = sequence.elements[0].instructions_image_base64
          // s.instructions_text_background = sequence.elements[0].instructions_text_background
        }
        if (sequence.elements[0].type == 'trigger_match_to_place') {
          s.preview_instructions_text = sequence.elements[0].preview_instructions_text
          s.preview_button_text = sequence.elements[0].preview_button_text
          s.instructions_text = sequence.elements[0].instructions_text
          s.instructions_button_text = sequence.elements[0].instructions_button_text
          s.show_preview = sequence.elements[0].show_preview
          s.smooth_time = sequence.elements[0].smooth_time
          s.max_speed = sequence.elements[0].max_speed
        }
        // trigger_event_received
        if (sequence.elements[0].type == 'trigger_event_received') {
          s.event_type = sequence.elements[0].event_type
          s.event_message = sequence.elements[0].event_message
        }
        // trigger_mqtt_message_received
        if (sequence.elements[0].type == 'trigger_mqtt_message_received') {
          s.host = sequence.elements[0].host
          s.username = sequence.elements[0].username
          s.password = sequence.elements[0].password
          s.read_topic = sequence.elements[0].read_topic
        }

        // trigger_amqp_message_received
        if (sequence.elements[0].type == 'trigger_amqp_message_received') {
          s.host = sequence.elements[0].host
          s.username = sequence.elements[0].username
          s.password = sequence.elements[0].password
          s.port = sequence.elements[0].port
          s.read_topic = sequence.elements[0].read_topic
          s.exchange = sequence.elements[0].exchange
          s.routing_key = sequence.elements[0].routing_key
          s.virtual_host = sequence.elements[0].virtual_host
          s.selected_token = sequence.elements[0].selected_token
          s.message = sequence.elements[0].message
        }

        // proximity triggers
        if (
          sequence.elements[0].type == 'trigger_proximity_enter' ||
          sequence.elements[0].type == 'trigger_proximity_exit'
        ) {
          s.distance = parseFloat(sequence.elements[0].distance)
        }

        if (sequence.elements[0].type == 'trigger_collision_enter' ||
            sequence.elements[0].type == 'trigger_collision_exit'
        ) {
          s.monitored_object = this.getMonitoredObjectTarget(sequence.elements[0])
        }

        for (let y = 1; y < sequence.elements.length; y++) {
          // console.log(sequence.elements[y])
          if (sequence.elements[y].type == 'parallel') {
            // console.log('parallel')
            s.actions.push(this.deserializeElement(sequence.elements[y].elements[0], 'after_previous'))
            for (let i = 1; i < sequence.elements[y].elements.length; i++) {
              s.actions.push(this.deserializeElement(sequence.elements[y].elements[i], 'with_previous'))
            }
          } else {
            // console.log('seq ')

            s.actions.push(this.deserializeElement(sequence.elements[y], 'after_previous'))
          }
        }
        if (sequence.name) s.name = sequence.name
        else s.name = `Sequence ${json.indexOf(sequence) + 1}`

        // Repeat
        if (sequence.repeat == -1 || sequence.repeat == true) s.repeat = true
        else s.repeat = false

        sequences.push(s)
      })
      return sequences
    },
    getHobjectAnimationFromNodePid(nodePid) {
      var hobjectPid = this.nodes.find(n => n.pid == nodePid).hobject_pid
      var h = this.hobjects.find(h => h.pid == hobjectPid)
      var abilities = JSON.parse(h.abilities)
      if (abilities.gltf_loader) return abilities.gltf_loader.animation_clip_names
    },
    getElementTarget: function(element) {
      var target = ''
      if (element.target) return { text: this.nodePidHobjectNameDictionary[element.target], value: element.target }
      else return target
    },
    getMonitoredObjectTarget: function(element) {
      var monitored_object = ''
      if (element.monitored_object) {
        return { text: this.nodePidHobjectNameDictionary[element.monitored_object], value: element.monitored_object }
      } 
      else {
        return monitored_object
      }
    },
    getElementTargets: function(element) {
      var targets = []
      if (element.targets && element.targets.length > 0)
        element.targets.forEach(target => {
          targets.push({ text: this.nodePidHobjectNameDictionary[target], value: target })
        })
      return targets
    },
    goToEditSpace() {
      this.$router.push({
        path: `/space/${this.space.pid}`,
      })
    },
    isNumeric(value) {
      return !isNaN(parseFloat(value)) && isFinite(value);
    },
    
    eulerToQuaternion(x, y, z) {
      console.log(`Converting Euler angles (${x}, ${y}, ${z}) to quaternion`);
      return { x: 0, y: 0, z: 0, w: 1 };
    },
    deserializeElement: function(element, start_after) {
      // create copy
      element = JSON.parse(JSON.stringify(element))
      var targets = []
      if (element.targets) {
        element.targets.forEach(target => {
          targets.push({ text: this.nodePidHobjectNameDictionary[target], value: target })
        })
      }
      element.targets = targets
      element.start = {
        value: start_after,
        text:
          this.actionsStartDic[start_after],
      }
      if (element.type == 'action_transform_to') {
        element.axis = element.axis ? element.axis : 'right'
        element.direction = element.direction ? element.direction : 'clockwise'
        element.to_position_x = element.to_position_x || null
        element.to_position_y = element.to_position_y || null
        element.to_position_z = element.to_position_z || null
        element.to_euler_rotation_x = element.to_euler_rotation_x || null
        element.to_euler_rotation_y = element.to_euler_rotation_y || null
        element.to_euler_rotation_z = element.to_euler_rotation_z || null
        element.to_local_scale = element.to_local_scale || null
      } 
      if (element.type == 'action_open_home') {
        if (element.space_pid && element.space_pid != '') {
          element.highlight_space = true
        } else {
          element.highlight_space = false
          element.space_pid = ''
        }
      }
      return element
    },

    /// SERIALIZER
    buildJsonSequenceElement: function(action) {
      // Duplicate action
      action = JSON.parse(JSON.stringify(action))
      // Action may or may not have associated target(s)
      if (action.targets && action.targets.length > 0) {
        var targets = []
        action.targets.forEach(target => {
          targets.push(target.value)
        })
        action.targets = targets
      }
      if (action.start) action.start = action.start.value
      // Temporary fix for action of type show_instructions.
      // If a base_64 image is provided, add a instructions_image_transparency property of 0 (opaque)
      if (action.type == 'action_show_instructions' && action.instructions_image_base64) {
        action.instructions_image_transparency = 0
      }
      if (action.type == 'action_open_home') {
        if (!action.highlight_space)
            action.space_pid = ''
      }
      return action
    },
    buildJsonparallel: function(actions) {
      var block = { type: 'parallel', elements: [] }
      for (let index = 0; index < actions.length; index++) {
        const action = actions[index]
        block.elements.push(this.buildJsonSequenceElement(action))
      }
      return block
    },
    buildJsonSequence: function() {
      var json = []
      this.sequences.forEach(sequence => {
        if (sequence.actions != null && sequence.actions.length > 0) {
          var el = {
            type: 'sequential',
            repeat: sequence.repeat == true ? -1 : 0,
            id: sequence.id,
            name: sequence.name,
            elements: [
              {
                type:
                  sequence.trigger.trigger_type != null && sequence.trigger.trigger_type.value != null
                    ? sequence.trigger.trigger_type.value
                    : null,
              },
            ],
          }
          el.elements[0].id = sequence.trigger.id
          if (sequence.trigger.trigger_type.value == 'trigger_screen_tap') {
            el.elements[0].instructions_text = sequence.instructions_text
            el.elements[0].show_instructions = sequence.show_instructions
            el.elements[0].instructions_image_base64 = sequence.instructions_image_base64
          }
          if (
            (sequence.trigger.trigger_type.value == 'trigger_image_anchor_found' ||
              sequence.trigger.trigger_type.value == 'trigger_image_anchor_lost' ||
            sequence.trigger.trigger_type.value == 'trigger_user_aligned_anchor' ) &&
            sequence.anchor
          ) {
            // trigger_image_anchor_found & trigger_image_anchor_lost
            // Add anchor to sequence trigger if needed
            el.elements[0].anchor = sequence.anchor.id
            el.elements[0].instructions_text = sequence.instructions_text
            el.elements[0].show_instructions = sequence.show_instructions
            el.elements[0].instructions_image_base64 = sequence.instructions_image_base64
            // el.elements[0].instructions_text_background = sequence.instructions_text_background
          }
          if (sequence.trigger.trigger_type.value == 'trigger_user_aligned_anchor') {
            el.elements[0].ok_button_text = sequence.ok_button_text
          }
          if (sequence.trigger.trigger_type.value == 'trigger_tap_to_place'||
              sequence.trigger.trigger_type.value == 'trigger_collision_enter' ||
            sequence.trigger.trigger_type.value == 'trigger_collision_exit') {
            el.elements[0].target = sequence.target ? sequence.target.value : null
          }
          if (sequence.trigger.trigger_type.value == 'trigger_match_to_place') {
            el.elements[0].target = sequence.target ? sequence.target.value : null
            // el.elements[0].targets = Array.from(sequence.targets, target => target.value)
            el.elements[0].preview_instructions_text = sequence.preview_instructions_text
            el.elements[0].preview_button_text = sequence.preview_button_text
            el.elements[0].instructions_button_text = sequence.instructions_button_text
            el.elements[0].show_preview = sequence.show_preview
            el.elements[0].smooth_time = sequence.smooth_time
            el.elements[0].max_speed = sequence.max_speed
          }
          // trigger_event_received
          if (sequence.trigger.trigger_type.value == 'trigger_event_received') {
            el.elements[0].event_type = sequence.event_type
            el.elements[0].event_message = sequence.event_message
          }
          // trigger_mqtt_message_received
          if (sequence.trigger.trigger_type.value == 'trigger_mqtt_message_received') {
            el.elements[0].host = sequence.host
            el.elements[0].username = sequence.username
            el.elements[0].password = sequence.password
            el.elements[0].read_topic = sequence.read_topic
          }
          //trigger_amqp_message_received
          if (sequence.trigger.trigger_type.value == 'trigger_amqp_message_received') {
            el.elements[0].host = sequence.host
            el.elements[0].port = sequence.port
            el.elements[0].username = sequence.username
            el.elements[0].password = sequence.password
            el.elements[0].virtual_host = sequence.virtual_host
            el.elements[0].exchange = sequence.exchange
            el.elements[0].routing_key = sequence.routing_key
            el.elements[0].selected_token = sequence.selected_token
            el.elements[0].message = sequence.message
          }

          // trigger_tap
          if (sequence.trigger.trigger_type.value == 'trigger_tap' && sequence.targets) {
            el.elements[0].targets = Array.from(sequence.targets, target => target.value)
          }
          // trigger_enter_portal
          if (sequence.trigger.trigger_type.value == 'trigger_enter_portal' && sequence.targets) {
            el.elements[0].targets = Array.from(sequence.targets, target => target.value)
          }
          // trigger_exit_portal
          if (sequence.trigger.trigger_type.value == 'trigger_exit_portal' && sequence.targets) {
            el.elements[0].targets = Array.from(sequence.targets, target => target.value)
          }
          // trigger_proximity_enter
          if (sequence.trigger.trigger_type.value == 'trigger_proximity_enter' && sequence.targets) {
            el.elements[0].targets = Array.from(sequence.targets, target => target.value)
            el.elements[0].distance = parseFloat(sequence.distance)
          }
          // trigger_proximity_exit
          if (sequence.trigger.trigger_type.value == 'trigger_proximity_exit' && sequence.targets) {
            el.elements[0].targets = Array.from(sequence.targets, target => target.value)
            el.elements[0].distance = parseFloat(sequence.distance)
          }
          // trigger_collision_enter
          if (sequence.trigger.trigger_type.value == 'trigger_collision_enter' && sequence.targets) {
            el.elements[0].targets = Array.from(sequence.targets, target => target.value)
            el.elements[0].monitored_object = sequence.monitored_object.value

          }
          // trigger_collision_exit
          if (sequence.trigger.trigger_type.value == 'trigger_collision_exit' && sequence.targets && sequence.monitored_object) {
            el.elements[0].targets = Array.from(sequence.targets, target => target.value)
            el.elements[0].monitored_object = sequence.monitored_object.value
          }
          // trigger_focus_in
          if (sequence.trigger.trigger_type.value == 'trigger_focus_in' && sequence.targets) {
            el.elements[0].targets = Array.from(sequence.targets, target => target.value)
          }
          // trigger_focus_out
          if (sequence.trigger.trigger_type.value == 'trigger_focus_out' && sequence.targets) {
            el.elements[0].targets = Array.from(sequence.targets, target => target.value)
          }
          if (sequence.actions != null && sequence.actions.length > 0) {
            // Add target to sequence trigger if needed
            // targets: [sequence.target != null && sequence.target.value != null ? sequence.target.value : null],

            for (let index = 0; index < sequence.actions.length; index++) {
              const action = sequence.actions[index]
              if (index == sequence.actions.length - 1 || sequence.actions[index + 1].start.value == 'after_previous') {
                el.elements.push(this.buildJsonSequenceElement(action))
              } else if (sequence.actions[index + 1].start.value == 'with_previous') {
                var parallel_actions = []
                parallel_actions.push(sequence.actions[index])
                index++
                parallel_actions.push(sequence.actions[index])
                let i = index + 1
                while (i < sequence.actions.length && sequence.actions[i].start.value == 'with_previous') {
                  parallel_actions.push(sequence.actions[i])
                  index++
                  i++
                }
                el.elements.push(this.buildJsonparallel(parallel_actions))
              } else {
                continue
              }
            }
          }
          json.push(el)
        }
      })
      return json
    },
    isValid: function(sequencing) {
      if (sequencing == null || !(sequencing.length > 0)) {
        // console.log('isValid false')
        return false
      } else {
        // console.log('isValid true')
        
        return true
      }
    },
 // Handle image removal from FilePond
    // This will set the base64 to null in the local sequence
    // so that it doesn't get uploaded again
    onInstructionImageRemoved: function (error, file) {
      if (!this.selected_action) return;
      this.selected_action.instructions_image_base64 = null;
    },

    async onInstructionImageAdded(error, file) {
      const resizeOptions = {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 512,
        useWebWorker: true,
      }

      // Compress the image file and extract base64 string
      const compressedFile = await imageCompression(file.file, resizeOptions);
      const base64 = await imageCompression.getDataUrlFromFile(compressedFile)

      //Remove the mime type from the base64 string
      const finalBase64 = base64.split(',')[1];
      this.selected_action.instructions_image_base64 = finalBase64; 
    },

    onFilePondInit: function() {
      // Initialize filePong with the instruction image in base64
      if (this.selected_action.instructions_image_base64) {
        if (this.$refs.instructionImageFilePond) {
          this.$refs.instructionImageFilePond.addFile( "data:image/png;base64," + this.selected_action.instructions_image_base64)
        }
      }
    },
    async SelectAction(action_id) {
      // Save current edits to selected_action_clone
      if (this.selected_action != null && this.selected_action_clone != null)
      {
        console.log ("Saving edits to selected_action_clone")
        console.log(this.selected_action_clone)
        this.selected_actions[this.selected_action_clone.id] = this.selected_action_clone
      }

      if (action_id == null) {
        this.selected_action = null
        this.is_action_inspector_open = false;
        return
      }

      this.selected_action = this.selected_actions.find(a => a.id == action_id)
      console.log('SelectAction')
      console.log(this.selected_action)

      // Open inspector if needed
      this.is_action_inspector_open = true

      // Populate material variants
      if (this.selected_action.type == 'action_set_materials_variant')
      {
        this.action_set_materials_variant_form_schema[0].type = 'text'
      }

      // Populate orbit center options
      if (this.selected_action.type == 'action_orbit')
      {
        // The center options list contains the list of all objects (nodePids) or the user. When user, the value is ''
        var center_options = { 
          '': 'User\'s current position'
        }
        this.nodes.forEach(n => {
          center_options[n.pid] = this.nodePidHobjectNameDictionary[n.pid]
        })
        this.action_orbit_form_schema[0].options = center_options
        
      }

      // Populate animation clip names
      if (
        this.selected_action.type == 'action_animate' &&
        this.getHobjectAnimationFromNodePid(this.selected_action.targets[0].value) &&
        this.getHobjectAnimationFromNodePid(this.selected_action.targets[0].value).length > 0
      ) {
        this.action_animate_form_schema[0].type = 'select'
        var anims = {}
        this.getHobjectAnimationFromNodePid(this.selected_action.targets[0].value).forEach(a => {
          anims[a] = a
        })
        this.action_animate_form_schema[0].options = anims
      } else {
        this.action_animate_form_schema[0].type = 'text'
      }
    },
    ToggleActionSidebar() {
      this.is_action_inspector_open = !this.is_action_inspector_open;
    },
    ShowAction(action_id, a) {
      this.selected_actions.forEach(a => {
        if (a.id != action_id) a.show = false
      })
      console.log('ShowAction')
      var action = this.selected_actions.find(a => a.id == action_id)
      console.log(a.show)
      if (action) {
        if (action.show == true) action.show = false
        else action.show = true
      }
      // console.log(action.show)
    },
    Back() {
      this.$router.go(-1)
    },
    onActionFormTransformToSubmitted(e) {
      // if payload is undefined, set value to null. This implies that no transformation will be applied for that position, rotation or scale component
      if (!e.payload) {
        this.selected_action[e.name] = null
      }
      if (e.name == 'to_euler_rotation_x' || e.name == 'to_euler_rotation_y' || e.name == 'to_euler_rotation_z') {
        // do we have values for x y and z rotation?
        if (
          this.isNumeric(e.payload.to_euler_rotation_x) &&
          this.isNumeric(e.payload.to_euler_rotation_y) &&
          this.isNumeric(e.payload.to_euler_rotation_z)
        ) {
          // Set quaternion value on e.payload based on to_angle_x, to_angle_y, to_angle_z
          const quaternion = HoverlayThreeUtils.eulerToQuaternion(e.to_euler_rotation_x, e.payload.to_euler_rotation_y, e.payload.to_euler_rotation_z)
          this.selected_action['to_rotation_x'] = quaternion.x
          this.selected_action['to_rotation_y'] = quaternion.y
          this.selected_action['to_rotation_z'] = quaternion.z
          this.selected_action['to_rotation_w'] = quaternion.w
        }
      }
      else {
        this.selected_action[e.name] = e.payload
      }
      // Mark as dirty when transform actions are changed
      this.emitSequencesChanged()
    },
    onActionFormTransformBySubmitted(e) {
      console.log("onActionFormTransformBySubmitted")
      console.log(e.name)
      console.log(e.payload)
      if (
        this.isNumeric(e.payload.by_angle_x) &&
        this.isNumeric(e.payload.by_angle_y) &&
        this.isNumeric(e.payload.by_angle_z)
      ) {
        // Set quaternion value on e.payload based on by_angle_x, by_angle_y, by_angle_z
        const quaternion = HoverlayThreeUtils.eulerToQuaternion(e.payload.by_angle_x, e.payload.by_angle_y, e.payload.by_angle_z)
        e.payload.by_quaternion_x = quaternion.x
        e.payload.by_quaternion_y = quaternion.y
        e.payload.by_quaternion_z = quaternion.z
        e.payload.by_quaternion_w = quaternion.w
      }
      this.selected_action[e.name] = e.payload
      // Mark as dirty when transform by actions are changed
      this.emitSequencesChanged()
    },
    onActionFormChanged(e) {
       this.selected_action[e.name] = e.payload
       // Mark as dirty when action properties are changed
       this.emitSequencesChanged()
    },
    onActionOpenHomeFormChanged(e) {
      console.log(e.name)
      console.log(e.payload)
      if (!e.highlight_space) {
        this.selected_action['space_pid'] = null
      }
    },
    // Sequence Name Editing
    // enableSequenceNameEditing: function() {
    //   this.temp_sequence_name = this.selected_sequence.name
    //   this.is_sequence_name_editing = true
    // },
    // disableSequenceNameEditing: function() {
    //   this.temp_sequence_name = null
    //   this.is_sequence_name_editing = false
    // },
    SaveSequenceName: function(name) {
      if (name) {
        this.selected_sequence.name = name
        // Mark as dirty when sequence names are changed
        this.emitSequencesChanged()
      }
    },
    sortBy(criteria) {
      console.log(`Sorting sequences by ${criteria}`);
    },
    getSequencesByTriggerType(triggerTypeValue) {
      return this.sequences.filter(seq => seq.trigger.trigger_type.value === triggerTypeValue);
    },
    showJsonWindow() {
      const jsonData = JSON.stringify(this.buildJsonSequence(), null, 2);
      const newWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
      
      if (!newWindow) {
        this.$vs.notify({
          title: 'Popup Blocked',
          text: 'Please allow popups for this site to view the JSON',
          color: 'warning',
          position: 'top-right'
        });
        return;
      }
      
      newWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Sequence JSON</title>
          <style>
            body {
              font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
              background-color: #f8f8f8;
              margin: 0;
              padding: 20px;
            }
            pre {
              background-color: #fff;
              padding: 15px;
              border-radius: 4px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              overflow: auto;
              max-height: calc(100vh - 100px);
              white-space: pre-wrap;
            }
            .toolbar {
              margin-bottom: 10px;
              display: flex;
              justify-content: space-between;
            }
            button {
              background-color: #5B3CC4;
              color: white;
              border: none;
              padding: 8px 16px;
              border-radius: 4px;
              cursor: pointer;
              font-size: 14px;
              margin-right: 10px;
            }
            button:hover {
              background-color: #4a30a0;
            }
            .json-key { color: #881391; }
            .json-string { color: #c41a16; }
            .json-number { color: #1c00cf; }
            .json-boolean { color: #0b6125; }
            .json-null { color: #808080; }
          </style>
        </head>
        <body>
          <div class="toolbar">
            <h2>Sequence JSON</h2>
            <div>
              <button onclick="navigator.clipboard.writeText(document.getElementById('jsonContent').textContent)">
                Copy to Clipboard
              </button>
              <button onclick="window.close()">Close</button>
            </div>
          </div>
          <pre id="jsonContent">${this.syntaxHighlight(jsonData)}</pre>
        </body>
        </html>
      `);
      
      newWindow.document.close();
    },
    
    syntaxHighlight(json) {
      if (!json) return ''; 
      
      // Escape HTML characters to prevent XSS
      json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      
      // Add syntax highlighting with regex
      return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g, match => {
        let cls = 'json-number';
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'json-key';
          } else {
            cls = 'json-string';
          }
        } else if (/true|false/.test(match)) {
          cls = 'json-boolean';
        } else if (/null/.test(match)) {
          cls = 'json-null';
        }
        return `<span class="${cls}">${match}</span>`;
      });
    },
    toggleQrCode() {
      this.showQrCode = !this.showQrCode;
    }
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/vuexy/components/vxTimeline.scss';

/* General Component Styling */
.con-vs-tabs, .con-vs-tabs .con-slot-tabs {
  overflow: visible !important;
}

.open-item > .vs-collapse-item--content {
  max-height: 99999px !important;
}

.open-item > * {
  overflow: visible !important;
}

.hover:hover {
  text-decoration: underline;
}

/* Timeline Base Styling */
.timeline {
  position: relative;
  padding: 0 0 0 24px;
  counter-reset: timeline-counter;
}

.timeline::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 6px;
  width: 2px;
  background-color: #d1d1d1;
  z-index: 0;
}

/* Timeline Slots and Elements */
.timeline-slot {
  position: relative;
}

.timeline-slot:has(.after-previous) {
  position: relative;
  margin-top: 8px;
}

/* Numbered Timeline Dots */
.timeline-slot:has(.after-previous)::before {
  content: counter(timeline-counter);
  counter-increment: timeline-counter;
  position: absolute;
  top: 50%;
  left: -19px;
  width: 20px;
  height: 20px;
  background-color: white;
  border: 2px solid rgba(var(--vs-primary), 0.7);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: bold;
  color: rgba(var(--vs-primary), 0.9);
}

/* Trigger Card Styling */
#selected_sequence\\.trigger\\.id {
  position: relative;
  margin-left: 0;
  padding: 6px 15px;
  border-radius: 8px;
  border-left: 3px solid #5B3CC4;
  border: 2px solid #5B3CC4;
  background-color: rgba(91, 60, 196, 0.04);
}

#selected_sequence\\.trigger\\.id::before {
  content: "0";
  position: absolute;
  top: 24px;
  left: -6px;
  width: 22px;
  height: 22px;
  background-color: #5B3CC4;
  border: 2px solid white;
  border-radius: 50%;
  transform: translateX(-50%);
  z-index: 2;
  font-size: 12px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

/* Trigger Card Class */
.trigger-card {
  border: 2px dashed #f8ba0b;
  background-color: white;
}

.trigger-card::before {
  background-color: #f8ba0b;
  border-color: white;
  transform: translate(-50%, -50%);
}

/* Hide Card Pseudo-element */
.card::before {
  display: none;
}

/* With-Previous Elements Styling */
.with-previous {
  position: relative;
  margin-top: 0px !important;
  margin-left: 20px;
  border-left: 3px solid rgba(var(--vs-primary), 0.7);
  padding-left: 10px;
  background-color: rgba(var(--vs-primary), 0.03);
  border-top: 1px solid #eaeaea;
  border-bottom: 1px solid #eaeaea;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

/* Consecutive With-Previous Elements */
.with-previous + .with-previous {
  border-top: none;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  margin-top: -1px;
}

/* First With-Previous After an After-Previous */
.after-previous + .with-previous {
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border-top: 1px solid #eaeaea;
}

/* Last With-Previous in a Group */
.with-previous:not(:has(+ .with-previous)) {
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  border-bottom: 1px solid #eaeaea;
}

/* Visual Grouping for With-Previous Elements */
.timeline-slot:has(.with-previous) {
  margin-top: -1px;
  position: relative;
}

/* Visual Grouping Between With-Previous Elements */
.timeline-slot:has(.with-previous) + .timeline-slot:has(.with-previous) .with-previous {
  border-top: 0;
  box-shadow: none;
}

/* Side Bracket for With-Previous Elements */
.timeline-slot:has(.with-previous)::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: -12px;
  width: 5px; 
  background-color: rgba(var(--vs-primary), 0.5);
  border-radius: 2px;
}

/* Connection Between After-Previous and With-Previous */
.timeline-slot:has(.after-previous) + .timeline-slot:has(.with-previous)::after {
  content: "";
  position: absolute;
  top: -35%;
  bottom: 0;
  left: -12px;
  width: 5px;
  background-color: rgba(var(--vs-primary), 0.5);
  border-radius: 2px;
}

/* Parent Element with With-Previous Children */
.timeline-slot:has(.after-previous) + .timeline-slot:has(.with-previous) .after-previous {
  border-left: 3px solid rgba(var(--vs-primary), 0.2);
  padding-left: 10px;
}

/* Form Element Styling */
.formulate-input {
  margin-bottom: 0.5rem !important;
}

.formulate-input[data-classification='box'] .formulate-input-wrapper {
  display: flex;
  align-items: center;
}

.formulate-input[data-classification='box'] .formulate-input-label--after {
  margin-left: 0.5em;
}

.formulate-input .formulate-input-label {
  margin-bottom: 0.1em;
}

/* Icon and Action Styling */
.icon-size > i {
  font-size: 1.6rem;
}

.action-type-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  left: 0px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: white;
}

.action-type-icon > i {
  font-size: 1rem;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 900;
}

.round-icon {
  border-radius: 50%;
  background-color: rgba(var(--vs-primary), 1);
}

/* Utility Classes */
.flex {
  display: flex;
  flex-wrap: wrap;
}

.text-gray {
  color: #8b8b8b;
}

/* VX Timeline Specific Overrides */
.vx-timeline > li,
.vx-timeline > div > li {
  background-color: #f7f6fe;
}

.vx-timeline > li:hover,
.vx-timeline > div > li:hover {
  background-color: #e6e4fc;
  border-radius: 5px;
}

.vx-timeline li {
  margin-bottom: 0px;
}

.action-title-highlight:hover {
  color: #2614ed;
}

/* Ensure the timeline has adequate space */
.timeline {
  min-height: 80vh;
}

.validation-errors {
  background-color: rgba(var(--vs-danger), 0.05);
  border-left: 4px solid rgba(var(--vs-danger), 0.7);
  padding: 10px;
  border-radius: 0 4px 4px 0;
  
  h4 {
    color: rgba(var(--vs-danger), 1);
    font-size: 0.9rem;
    font-weight: 600;
  }
  
  ul {
    padding-left: 20px;
    margin-bottom: 0;
    
    li {
      font-size: 0.85rem;
      margin-bottom: 4px;
      color: rgba(var(--vs-danger), 0.8);
    }
  }
}

.text-danger {
  color: rgba(var(--vs-danger), 1) !important;
}

/* Add to your CSS */
.sidebar-section {
  border-bottom: 1px solid rgba(0,0,0,0.05);
  margin-bottom: 10px;
  padding-bottom: 10px;
}

.sequence-section-header {
  display: flex;
  align-items: center;
  padding: 8px 0;
  margin-bottom: 5px;
  background-color: rgba(var(--vs-primary), 0.03);
  border-radius: 4px;
}

.sequence-group {
  margin-bottom: 15px;
}

.sequence-count {
  font-size: 0.7rem;
  background-color: rgba(var(--vs-primary), 0.1);
  color: rgba(var(--vs-primary), 1);
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 8px;
}

/* Enhanced selection styles */
.sequence-item {
  padding: 6px 4px;
  font-size: 0.85rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

/* Sidebar width and scrolling */
.vs-sidebar {
  max-width: 400px;
  overflow-y: auto;
}

/* Optimize trigger type headers */
.trigger-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 4px;
}

.trigger-title {
  font-size: 0.85rem;
  font-weight: 600;
  max-width: 75%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* More compact sequence items */
.sequence-item {
  padding: 6px 4px;
  font-size: 0.85rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

/* Smaller margin for sequence items */
.ml-4 {
  margin-left: 0.75rem !important;
}

/* Reduce padding on columns */
.sm\:p-0.p-4 {
  padding: 0.5rem !important;
}

/* More compact buttons */
.new-sequence-btn {
  min-width: auto;
  padding-left: 8px;
  padding-right: 8px;
}

/* Make QR code section collapsible */
.qr-code-section {
  padding: 0.5rem;
}

.qr-code-section.collapsed .qr-content {
  display: none;
}

.qr-code {
  max-width: 100%;
}

/* Updated sequence-row styles */
.sequence-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px;
  border-radius: 4px;
  
  &:hover {
    background-color: rgba(var(--vs-primary), 0.05);
  }
}

.sequence-name {
  flex: 1;
  cursor: pointer;
  padding-left: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  &.active {
    font-weight: 600;
    color: rgba(var(--vs-primary), 1);
  }
}

.sequence-actions {
  display: flex;
  align-items: center;
}
</style>